import React, { useContext, useMemo } from "react";

import { Box, Flex, Stack, Text, xcss } from "@atlaskit/primitives";
import Button from "@atlaskit/button";
import { events } from "@forge/bridge";
import { token } from "@atlaskit/tokens";
import { useTranslation } from "react-i18next";

import { useScreenClass } from "react-grid-system";
import StyledText from "../../../primitives/StyledText";
import { BannerContext } from "../../context";
import { formatPriceWithCurrency } from "../../../../functions";
import { AppContext } from "../../../../../Context";
import ProductItem from "./helpers/ProductItem";

const OrderSummary = () => {
  const { t } = useTranslation();

  const { itemsInCart } = useContext(BannerContext);

  return (
    <Flex
      alignItems="center"
      justifyContent="space-between"
      columnGap="space.200"
      xcss={xcss({
        paddingBlockStart: "space.250",
        paddingInline: "space.250",
      })}
    >
      <StyledText
        color="color.text"
        customStyles={{
          fontSize: "12px",
          fontWeight: "600",
          lineHeight: "16px",
          textTransform: "uppercase",
        }}
      >
        {t("order-summary")}
      </StyledText>

      <Text color="color.text.subtlest">
        {itemsInCart?.length}{" "}
        {t(itemsInCart?.length === 1 ? "item" : "items").toLowerCase()}
      </Text>
    </Flex>
  );
};

const Total = () => {
  const { t } = useTranslation();

  const { totalPrice, config: { currency } = {} } = useContext(BannerContext);
  const { forgeContext: { localeCode } = {} } = useContext(AppContext);

  return (
    <Flex
      alignItems="center"
      justifyContent="space-between"
      columnGap="space.200"
      xcss={xcss({
        paddingInline: "space.250",
      })}
    >
      <Text color="color.text">{t("total")}</Text>
      <StyledText color="color.text" customStyles={{ fontWeight: "600" }}>
        {formatPriceWithCurrency({
          price: totalPrice,
          locale: localeCode,
          currency,
        })}
      </StyledText>
    </Flex>
  );
};

const Actions = () => {
  const { t } = useTranslation();

  const screenClass = useScreenClass();
  const isMobile = ["xs", "sm"].includes(screenClass);

  return (
    <Flex
      alignItems="center"
      columnGap="space.200"
      justifyContent="end"
      xcss={xcss({ paddingBlockEnd: "space.250", paddingInline: "space.250" })}
    >
      <Button
        onClick={() => events.emit("OPEN_CATALOG_BROWSER")}
        appearance="primary"
      >
        {t("continue-shopping")}
      </Button>
      <Button
        onClick={() =>
          isMobile
            ? events.emit("OPEN_CATALOG_BROWSER", { openPreviewCart: true })
            : events.emit("OPEN_CATALOG_BROWSER", { openCart: true })
        }
      >
        {t("edit-cart")}
      </Button>
    </Flex>
  );
};

const ItemsInCart = () => {
  const { itemsInCart } = useContext(BannerContext);

  return (
    <Box
      style={{
        maxHeight: "368px",
        overflowY: "auto",
        paddingInline: "20px 20px",
        scrollbarGutter: "stable",
        scrollbarColor: `${token("color.background.neutral")} transparent`,
        scrollbarWidth: "thin",
      }}
    >
      <Flex direction="column" rowGap="space.200">
        {itemsInCart.map((item) => (
          <ProductItem key={`${item.id}`} item={item} />
        ))}
      </Flex>
    </Box>
  );
};

const Divider = () => (
  <Box paddingInline="space.250">
    <Box
      style={{
        height: "1px",
        width: "100%",
        borderBottom: `1px solid ${token("color.border")}`,
      }}
    />
  </Box>
);

function FilledOrder() {
  return (
    <Box backgroundColor="elevation.surface.overlay">
      <Stack space="space.200">
        <OrderSummary />

        <Divider />

        <ItemsInCart />

        <Divider />

        <Total />

        <Divider />

        <Actions />
      </Stack>
    </Box>
  );
}

export default FilledOrder;
